import { useLocation, useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect, useRef, useState } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  generateFormRules,
  generateQueryString,
  getErrorMessage,
  BaseResponseProps,
} from '@qlibs/react-components';
import RowFilter from '@qlibs/react-components/dist/Table/RowFilter';
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Grid,
  Image,
  Input,
  MenuProps,
  Modal,
  Row,
  Select,
  Table,
  Tabs,
  Tag,
  TimePicker,
  message,
} from 'antd';
import { httpRequest } from '../../helpers/api';
import { EDateFormat, SUPER_ADMIN_ROLE_ID } from '../../const/config';
import { AssetProperties } from '../asset/types/asset.type';
import LinkTable from '../../components/LinkTable';
import {
  ReportEmailReceiverProperties,
  ReportEmailTargetAssetProperties,
  ReportEmailTemplateProperties,
  ETemplateType,
  TEMPLATE_TYPE_OPTIONS,
} from './types/report.type';
import useGroup from '../../zustand/useGroup';
import useMasterData from '../../hooks/useMasterData';
import { useAuthUser } from 'react-auth-kit';
import dayjs from 'dayjs';
import {
  formatCurrentDate,
  EFormatCurrentDate,
} from '../../helpers/formatCurrentDate';
import {
  getErrorMessage as getHttpErrorMessage,
  getErrorStatus,
} from '../../helpers/httpError';
import EmailLog from './emailLog';
import { formatReportSendingTime } from './helpers/emailSchedule';
import {
  CustomFormItemPer1,
  CustomFormItemPer2,
} from '../user/components/CustomFormItem';
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { RiEqualizer2Line } from '@remixicon/react';
import {
  MasterReportMobileActionButton,
  MasterReportMobileCustomEmailActions,
  MasterReportMobileCustomEmailBody,
  MasterReportMobileCustomEmailCancelButton,
  MasterReportMobileCustomEmailModal,
  MasterReportMobileCustomEmailSubmitButton,
  MasterReportMobileCustomEmailTitle,
  MasterReportMobileEmptyState,
  MasterReportMobileFilterActions,
  MasterReportMobileFilterApplyButton,
  MasterReportMobileFilterButton,
  MasterReportMobileFilterDrawer,
  MasterReportMobileFilterField,
  MasterReportMobileFilterFields,
  MasterReportMobileFilterForm,
  MasterReportMobileFilterRow,
  MasterReportMobileFilterSelect,
  MasterReportMobileHeader,
  MasterReportMobileLinkText,
  MasterReportMobileMoreButton,
  MasterReportMobileMoreIcon,
  MasterReportMobilePage,
  MasterReportMobilePageButton,
  MasterReportMobilePagination,
  MasterReportMobilePaginationActions,
  MasterReportMobilePaginationInfo,
  MasterReportMobileSearch,
  MasterReportMobileStatus,
  MasterReportMobileTabButton,
  MasterReportMobileTable,
  MasterReportMobileTableWrap,
  MasterReportMobileTabs,
} from './mobile';

const moment = require('moment');

const FEATURE = 'MASTER_REPORT';
const DEFAULT_NO_DATA_CUSTOM_EMAIL_MESSAGE =
  'No report data for the selected filter. Email delivery was canceled.';

const MasterReport = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();

  const handleCreate = () => {
    navigate({
      pathname: `/master-report/${
        activeTab === 1
          ? 'email-template/add'
          : activeTab === 2
            ? 'email-receiver/add'
            : 'email-target-by-asset/add'
      }`,
    });
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState<boolean>(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = useState<boolean>(false);
  const [isModalCustomEmailOpen, setIsModalCustomEmailOpen] =
    useState<boolean>(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [mobileFilterValues, setMobileFilterValues] = useState({
    company: '',
    status: 'All',
    templateType: 'All',
    filterData: 'default',
  });
  const [currentData, setCurrentData] = useState<any>();
  const [forceDeleteId, setForceDeleteId] = useState<string>();
  const [activeTab, setActiveTab] = useState<any>(1);
  const activeTabKey = Number(activeTab);
  const [expandedRowKey, setExpandedRowKey] = React.useState<any>();

  const { company } = useGroup();
  const { companies, getListCompanies } = useMasterData();
  const auth = useAuthUser();
  const emailLogRef = useRef<any>(null);

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
    fetchList,
    setIsLoading,
  } = useFetchList<any>({
    httpRequest: httpRequest as any,
    endpoint: `${
      activeTab === 1
        ? 'report-email-template'
        : activeTab === 2
          ? 'report-email-receiver'
          : 'report-email-target-asset'
    }`,
    limit: +PAGE_SIZE_OPTIONS[1],
    pageQuery: {
      defaultValue: {
        page: 1,
      },
    },
  });

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId &&
          forceDeleteId ===
            currentData[
              `${
                activeTab === 1
                  ? 'templateId'
                  : activeTab === 2
                    ? 'reportEmailReceiverId'
                    : 'reportEmailTargetAssetId'
              }`
            ];

        const url =
          activeTab === 1
            ? '/report-email-template/'
            : activeTab === 2
              ? '/report-email-receiver/'
              : '/report-email-target-asset/';

        if (activeTab !== 1) {
          await httpRequest.delete(
            url +
              currentData[
                `${
                  activeTab === 2
                    ? 'reportEmailReceiverId'
                    : 'reportEmailTargetAssetId'
                }`
              ],
            {
              params: {
                forceDelete: forceDelete || undefined,
              },
            },
          );
        } else {
          await httpRequest.delete(url, {
            params: {
              forceDelete: forceDelete || undefined,
              templateId: currentData['templateId'],
              externalId: currentData['externalId'],
            },
          });
        }

        message.success(`Success ${forceDelete ? 'delete' : 'archive'}`);

        setQuery({
          ...query,
          // [`${
          //   activeTab === 1
          //     ? "externalId"
          //     : activeTab === 2
          //     ? "filterCompanyId"
          //     : "filterCompanyId"
          // }`]: company?.companyId || "",
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        const url =
          activeTab === 1
            ? '/report-email-template/'
            : activeTab === 2
              ? '/report-email-receiver/'
              : '/report-email-target-asset/';

        await httpRequest.patch(
          url +
            currentData[
              `${
                activeTab === 1
                  ? 'templateId'
                  : activeTab === 2
                    ? 'reportEmailReceiverId'
                    : 'reportEmailTargetAssetId'
              }`
            ] +
            '/restore',
        );
        message.success(`Success restore`);

        setQuery({
          ...query,
          // [`${
          //   activeTab === 1
          //     ? "externalId"
          //     : activeTab === 2
          //     ? "filterCompanyId"
          //     : "filterCompanyId"
          // }`]: company?.companyId || "",
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestore(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find(
      (item) =>
        item[
          `${
            activeTab === 1
              ? 'templateId'
              : activeTab === 2
                ? 'reportEmailReceiverId'
                : 'reportEmailTargetAssetId'
          }`
        ] === id,
    );
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDelete(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find(
      (item) =>
        item[
          `${
            activeTab === 1
              ? 'templateId'
              : activeTab === 2
                ? 'reportEmailReceiverId'
                : 'reportEmailTargetAssetId'
          }`
        ] === id,
    );
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find(
        (item) =>
          item[
            `${
              activeTab === 1
                ? 'templateId'
                : activeTab === 2
                  ? 'reportEmailReceiverId'
                  : 'reportEmailTargetAssetId'
            }`
          ] === id && item.deletedAt,
      );
      if (found) {
        return true;
      }
    }

    return false;
  }

  const columnsEmail: IAppTableColumn<ReportEmailReceiverProperties>[] = [
    {
      title: 'TARGET EMAIL',
      dataIndex: 'name',
      key: 'reportEmailReceiverId',
      render: (value, records) => (
        <div>
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          {records?.user?.email}
        </div>
      ),
    },
    {
      title: 'COMPANY',
      dataIndex: 'company',
      key: 'company',
      render: (value, records) => <div>{records?.company?.name}</div>,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      type: 'actions',
      actions: [
        isUserHasPermission([FEATURE + '.UPDATE'])
          ? {
              key: 'update',
              label: 'Update',
              onPress: (id) =>
                navigate('/master-report/email-receiver/' + id + '/edit'),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.DELETE'])
          ? {
              key: 'archive',
              label: 'Delete',
              color: '#ff4d4f',
              disabled: (id) => isArchived(id),
              disabledTooltip: 'This data is already archived',
              onPress: (id) => handleDelete(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.RESTORE'])
          ? {
              key: 'restore',
              label: 'Restore',
              color: '#1890ff',
              disabled: (id) => !isArchived(id),
              disabledTooltip: 'This data not in archived',
              onPress: (id) => handleRestore(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.FORCE_DELETE'])
          ? {
              key: 'delete',
              label: 'Force Delete',
              onPress: (id) => handleDelete(id, true),
            }
          : undefined,
      ],
    },
  ];

  const columnsTemplate: IAppTableColumn<ReportEmailTemplateProperties>[] = [
    {
      title: 'TEMPLATE NAME',
      dataIndex: 'templateName',
      key: 'templateId',
      render: (value, records) => (
        <LinkTable
          onPress={() =>
            navigate(
              '/master-report/email-template/' + records.templateId + '/edit',
            )
          }
        >
          {records.deletedAt ? (
            <Tag color="#ff5500" className="mb-2">
              Archived
            </Tag>
          ) : (
            false
          )}
          {records?.templateName}
        </LinkTable>
      ),
    },
    {
      title: 'TYPE',
      dataIndex: 'templateType',
      key: 'templateType',
      render: (value: string) => (
        <Tag color={value === 'operation_mode' ? 'blue' : 'default'}>
          {value === 'operation_mode' ? 'Operation Mode' : 'Daily Report'}
        </Tag>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value ? 'success' : 'error'}>
          {value ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      type: 'actions',
      actions: [
        isUserHasPermission([FEATURE + '.UPDATE'])
          ? {
              key: 'update',
              label: 'Update',
              onPress: (id) =>
                navigate('/master-report/email-template/' + id + '/edit'),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.DELETE'])
          ? {
              key: 'archive',
              label: 'Delete',
              color: '#ff4d4f',
              disabled: (id) => isArchived(id),
              disabledTooltip: 'This data is already archived',
              onPress: (id) => handleDelete(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.RESTORE'])
          ? {
              key: 'restore',
              label: 'Restore',
              color: '#1890ff',
              disabled: (id) => !isArchived(id),
              disabledTooltip: 'This data not in archived',
              onPress: (id) => handleRestore(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.FORCE_DELETE'])
          ? {
              key: 'delete',
              label: 'Force Delete',
              onPress: (id) => handleDelete(id, true),
            }
          : undefined,
      ],
    },
  ];

  const columnsEmailTargetAsset: IAppTableColumn<ReportEmailTargetAssetProperties>[] =
    [
      {
        title: 'ASSET',
        dataIndex: 'name',
        key: 'reportEmailTargetAssetId',
        render: (value, records) => (
          <LinkTable
            onPress={() =>
              navigate(
                '/master-report/email-target-by-asset/' +
                  records.reportEmailTargetAssetId +
                  '/edit',
              )
            }
          >
            {records.deletedAt ? (
              <Tag color="#ff5500" className="mb-2">
                Archived
              </Tag>
            ) : (
              false
            )}
            {records?.asset?.name || records?.assetId || '-'}
          </LinkTable>
        ),
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        key: 'status',
        render: (value, _) => (
          <Tag color={value === 'active' ? 'success' : 'error'}>
            {value?.toUpperCase() || 'N/A'}
          </Tag>
        ),
      },
      {
        title: 'FREQUENCY',
        dataIndex: 'frequency',
        key: 'frequency',
        render: (value) => value || '-',
      },
      {
        title: 'SCHEDULE',
        dataIndex: 'sendingAt',
        key: 'sendingAt',
        render: (_, records) => {
          if (!records.frequency || !records.sendingAt) return '-';
          return (
            <span>
              {records.frequency === 'DAILY'
                ? `Every day at ${formatReportSendingTime(
                    records.sendingAt,
                    records.timezoneOffset,
                  )}`
                : formatCurrentDate(
                    records.sendingAt,
                    EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                  )}
            </span>
          );
        },
      },
      {
        title: 'TOTAL EMAIL RECEIVER',
        dataIndex: 'totalTargetEmail',
        key: 'totalTargetEmail',
        render: (_, records) => records.reportEmailTarget?.length,
      },
      {
        title: 'UPDATED AT',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value, _) =>
          formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
      },
      {
        title: 'ACTION',
        key: 'action',
        type: 'actions',
        actions: [
          isUserHasPermission([FEATURE + '.UPDATE'])
            ? {
                key: 'update',
                label: 'Update',
                onPress: (id) =>
                  navigate(
                    '/master-report/email-target-by-asset/' + id + '/edit',
                  ),
              }
            : undefined,
          isUserHasPermission([FEATURE + '.DELETE'])
            ? {
                key: 'archive',
                label: 'Delete',
                color: '#ff4d4f',
                disabled: (id) => isArchived(id),
                disabledTooltip: 'This data is already archived',
                onPress: (id) => handleDelete(id),
              }
            : undefined,
          isUserHasPermission([FEATURE + '.RESTORE'])
            ? {
                key: 'restore',
                label: 'Restore',
                color: '#1890ff',
                disabled: (id) => !isArchived(id),
                disabledTooltip: 'This data not in archived',
                onPress: (id) => handleRestore(id),
              }
            : undefined,
          isUserHasPermission([FEATURE + '.FORCE_DELETE'])
            ? {
                key: 'delete',
                label: 'Force Delete',
                onPress: (id) => handleDelete(id, true),
              }
            : undefined,
        ],
      },
    ];

  const filterCompanyOptions = React.useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [{ value: '', label: 'All' }, ...data];
  }, [companies]);

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: `Search by ${
        activeTab === 1 ? 'template name' : activeTab === 2 ? 'email' : 'asset'
      }`,
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'company',
      label: 'Company',
      options: filterCompanyOptions,
      value: pageQueries['filterCompanyId'] || 'All',
      onChange: (value: any) => {
        filterDropdown({
          ['filterCompanyId']: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
      hide: activeTab === 1,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'All',
          label: 'All',
        },
        {
          value: `${activeTab === 1 ? true : 'active'}`,
          label: 'Active',
        },
        {
          value: `${activeTab === 1 ? false : 'inactive'}`,
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          [`${activeTab === 1 ? 'status' : 'filterStatus'}`]:
            value === 'All' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'templateType',
      label: 'Template Type',
      options: [
        { value: 'All', label: 'All' },
        ...TEMPLATE_TYPE_OPTIONS.map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
      ],
      onChange: (value: any) => {
        filterDropdown({
          templateType: value === 'All' ? '' : value,
        });
      },
      colSpan: 4,
      hide: activeTab !== 1,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter(
    (item) =>
      !(item.key === 'company' && activeTab === 1) &&
      (!isUserHasPermission([FEATURE + '.DELETE'])
        ? item.key !== 'company'
        : !item.hide
          ? true
          : false),
  );

  useEffect(() => {
    getListCompanies();
  }, []);

  React.useEffect(() => {
    if (!isMobileFilterOpen) {
      return;
    }

    setMobileFilterValues({
      company: pageQueries.filterCompanyId || pageQueries.company || '',
      status: pageQueries.status || pageQueries.filterStatus || 'All',
      templateType: pageQueries.templateType || 'All',
      filterData:
        pageQueries.filterArchived || pageQueries.filterData || 'default',
    });
  }, [activeTabKey, isMobileFilterOpen, pageQueries]);

  const mobilePagination = {
    current: Number((pagination as any)?.page || pageQueries.page || 1),
    pageSize: Number(
      (pagination as any)?.limit || query.limit || +PAGE_SIZE_OPTIONS[1],
    ),
    total: Number((pagination as any)?.total || data.length),
  };
  const mobileCurrentPage = mobilePagination.current;
  const mobilePageSize = mobilePagination.pageSize;
  const mobileTotal = mobilePagination.total;
  const mobileTotalPages = Math.max(1, Math.ceil(mobileTotal / mobilePageSize));
  const mobileStartResult =
    mobileTotal === 0 ? 0 : (mobileCurrentPage - 1) * mobilePageSize + 1;
  const mobileEndResult = Math.min(
    mobileCurrentPage * mobilePageSize,
    mobileTotal,
  );

  const handleMobilePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > mobileTotalPages) {
      return;
    }

    changePage(nextPage, mobilePageSize);
  };

  const getMobileSearchPlaceholder = () => {
    if (activeTabKey === 1) {
      return 'Search by template name';
    }

    return 'Search by asset';
  };

  const handleApplyMobileFilter = () => {
    const filterPayload: Record<string, any> = {
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    };

    if (activeTabKey === 1) {
      filterPayload.status =
        mobileFilterValues.status === 'All' ? '' : mobileFilterValues.status;
      filterPayload.templateType =
        mobileFilterValues.templateType === 'All'
          ? ''
          : mobileFilterValues.templateType;
    } else {
      filterPayload.filterCompanyId = mobileFilterValues.company;
      filterPayload.filterStatus =
        mobileFilterValues.status === 'All' ? '' : mobileFilterValues.status;
    }

    filterDropdown(filterPayload);
    setIsMobileFilterOpen(false);
  };

  const getMobileRecordId = (record: any) => {
    if (activeTabKey === 1) {
      return record.templateId;
    }

    if (activeTabKey === 2) {
      return record.reportEmailReceiverId;
    }

    return record.reportEmailTargetAssetId;
  };

  const getMobileActionItems = (record: any): MenuProps['items'] => {
    const recordId = getMobileRecordId(record);
    const items: MenuProps['items'] = [];

    if (isUserHasPermission([FEATURE + '.UPDATE'])) {
      items.push({ key: 'update', label: 'Update' });
    }

    if (isUserHasPermission([FEATURE + '.DELETE'])) {
      items.push({
        key: 'archive',
        label: 'Delete',
        danger: true,
        disabled: isArchived(recordId),
      });
    }

    if (isUserHasPermission([FEATURE + '.RESTORE'])) {
      items.push({
        key: 'restore',
        label: 'Restore',
        disabled: !isArchived(recordId),
      });
    }

    if (isUserHasPermission([FEATURE + '.FORCE_DELETE'])) {
      items.push({ key: 'delete', label: 'Force Delete', danger: true });
    }

    return items;
  };

  const handleMobileActionClick =
    (record: any) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      const recordId = getMobileRecordId(record);

      if (info?.key === 'update') {
        if (activeTabKey === 1) {
          navigate('/master-report/email-template/' + recordId + '/edit');
        } else if (activeTabKey === 2) {
          navigate('/master-report/email-receiver/' + recordId + '/edit');
        } else {
          navigate(
            '/master-report/email-target-by-asset/' + recordId + '/edit',
          );
        }
      }

      if (info?.key === 'archive') {
        handleDelete(recordId);
      }

      if (info?.key === 'restore') {
        handleRestore(recordId);
      }

      if (info?.key === 'delete') {
        handleDelete(recordId, true);
      }
    };

  const renderMobileActionCell = (record: any) => {
    const items = getMobileActionItems(record);

    if (!items?.length) {
      return null;
    }

    return (
      <Dropdown
        trigger={['click']}
        menu={{
          items,
          onClick: handleMobileActionClick(record),
        }}
      >
        <MasterReportMobileMoreButton
          type="button"
          aria-label="Open master report action"
        >
          <MasterReportMobileMoreIcon />
        </MasterReportMobileMoreButton>
      </Dropdown>
    );
  };

  const getTemplateStatus = (value: any) =>
    value === true || value === 'true' || value === 'active';

  const renderMobileTemplateTable = () => {
    if (isLoading) {
      return (
        <MasterReportMobileEmptyState>
          Loading email templates...
        </MasterReportMobileEmptyState>
      );
    }

    if (!data.length) {
      return (
        <MasterReportMobileEmptyState>
          No email template data found.
        </MasterReportMobileEmptyState>
      );
    }

    return (
      <MasterReportMobileTableWrap>
        <MasterReportMobileTable $minWidth={780}>
          <thead>
            <tr>
              <th style={{ width: 170 }}>Template Name</th>
              <th style={{ width: 130 }}>Type</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 150 }}>Created at</th>
              <th style={{ width: 150 }}>Update at</th>
              <th style={{ width: 80 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item: ReportEmailTemplateProperties) => {
              const isActive = getTemplateStatus(item.status);

              return (
                <tr key={item.templateId}>
                  <td>
                    <MasterReportMobileLinkText
                      type="button"
                      onClick={() =>
                        navigate(
                          '/master-report/email-template/' +
                            item.templateId +
                            '/edit',
                        )
                      }
                    >
                      {item.templateName || '-'}
                    </MasterReportMobileLinkText>
                  </td>
                  <td>
                    {item.templateType === 'operation_mode'
                      ? 'Operation Mode'
                      : 'Daily Report'}
                  </td>
                  <td>
                    <MasterReportMobileStatus $active={isActive}>
                      {isActive ? 'Active' : 'Inactive'}
                    </MasterReportMobileStatus>
                  </td>
                  <td>
                    {item.createdAt
                      ? formatCurrentDate(
                          item.createdAt,
                          EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                        )
                      : '-'}
                  </td>
                  <td>
                    {item.updatedAt
                      ? formatCurrentDate(
                          item.updatedAt,
                          EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                        )
                      : '-'}
                  </td>
                  <td>{renderMobileActionCell(item)}</td>
                </tr>
              );
            })}
          </tbody>
        </MasterReportMobileTable>
      </MasterReportMobileTableWrap>
    );
  };

  const renderMobileEmailTargetTable = () => {
    const emailTargetAssetTableData: ReportEmailTargetAssetProperties[] =
      isLoading
        ? []
        : data.filter((item: ReportEmailTargetAssetProperties) =>
            Boolean(item.asset),
          );

    if (isLoading) {
      return (
        <MasterReportMobileEmptyState>
          Loading email targets...
        </MasterReportMobileEmptyState>
      );
    }

    if (!emailTargetAssetTableData.length) {
      return (
        <MasterReportMobileEmptyState>
          No email target data found.
        </MasterReportMobileEmptyState>
      );
    }

    return (
      <MasterReportMobileTableWrap>
        <MasterReportMobileTable $minWidth={879}>
          <thead>
            <tr>
              <th style={{ width: 165 }}>Asset</th>
              <th style={{ width: 93 }}>Status</th>
              <th style={{ width: 109 }}>Frequency</th>
              <th style={{ width: 134 }}>Schedule</th>
              <th style={{ width: 160 }}>Total Email Receiver</th>
              <th style={{ width: 132 }}>Update at</th>
              <th style={{ width: 86 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {emailTargetAssetTableData.map(
              (item: ReportEmailTargetAssetProperties) => {
                const isActive = item.status === 'active';

                return (
                  <tr key={item.reportEmailTargetAssetId}>
                    <td>
                      <MasterReportMobileLinkText
                        type="button"
                        onClick={() =>
                          navigate(
                            '/master-report/email-target-by-asset/' +
                              item.reportEmailTargetAssetId +
                              '/edit',
                          )
                        }
                      >
                        {item.asset?.name || item.assetId || '-'}
                      </MasterReportMobileLinkText>
                    </td>
                    <td>
                      <MasterReportMobileStatus $active={isActive}>
                        {isActive ? 'Active' : 'Inactive'}
                      </MasterReportMobileStatus>
                    </td>
                    <td>{item.frequency || '-'}</td>
                    <td>
                      {!item.frequency || !item.sendingAt
                        ? '-'
                        : item.frequency === 'DAILY'
                          ? `Every day at ${formatReportSendingTime(
                              item.sendingAt,
                              item.timezoneOffset,
                            )}`
                          : formatCurrentDate(
                              item.sendingAt,
                              EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                            )}
                    </td>
                    <td>{item.reportEmailTarget?.length || 0}</td>
                    <td>
                      {formatCurrentDate(
                        item.updatedAt,
                        EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                      )}
                    </td>
                    <td>{renderMobileActionCell(item)}</td>
                  </tr>
                );
              },
            )}
          </tbody>
        </MasterReportMobileTable>
      </MasterReportMobileTableWrap>
    );
  };

  const renderMobilePagination = () => (
    <MasterReportMobilePagination>
      <MasterReportMobilePaginationInfo>
        Showing {mobileStartResult} to {mobileEndResult} of {mobileTotal}{' '}
        results
      </MasterReportMobilePaginationInfo>
      <MasterReportMobilePaginationActions>
        <MasterReportMobilePageButton
          type="button"
          disabled={mobileCurrentPage <= 1}
          onClick={() => handleMobilePageChange(mobileCurrentPage - 1)}
          aria-label="Previous page"
        >
          <LeftOutlined />
        </MasterReportMobilePageButton>
        <MasterReportMobilePageButton type="button" $active>
          {mobileCurrentPage}
        </MasterReportMobilePageButton>
        <MasterReportMobilePageButton
          type="button"
          disabled={mobileCurrentPage >= mobileTotalPages}
          onClick={() => handleMobilePageChange(mobileCurrentPage + 1)}
          aria-label="Next page"
        >
          <RightOutlined />
        </MasterReportMobilePageButton>
      </MasterReportMobilePaginationActions>
    </MasterReportMobilePagination>
  );

  const renderMobileListContent = () => {
    if (activeTabKey === 4) {
      return <EmailLog ref={emailLogRef} isMobile />;
    }

    return (
      <>
        <MasterReportMobileFilterRow>
          <MasterReportMobileSearch
            allowClear
            defaultValue={pageQueries.search}
            placeholder={getMobileSearchPlaceholder()}
            suffix={<SearchOutlined style={{ color: '#9facbf' }} />}
            onChange={(event) => handleSearch(event.target.value)}
          />
          <MasterReportMobileFilterButton
            type="button"
            aria-label="Open master report filters"
            onClick={() => setIsMobileFilterOpen(true)}
          >
            <RiEqualizer2Line size={20} />
          </MasterReportMobileFilterButton>
        </MasterReportMobileFilterRow>

        {activeTabKey === 1
          ? renderMobileTemplateTable()
          : renderMobileEmailTargetTable()}
        {renderMobilePagination()}
      </>
    );
  };

  const _renderEmailReceivers = () => {
    return (
      <div style={{ marginTop: 15 }}>
        <RowFilter
          filterValues={{
            search: pageQueries.search,
            status: pageQueries.filterStatus || 'All',
            filterData: pageQueries.filterData || 'default',
            company: company.name || 'all',
          }}
          filters={[filterData]}
        />
        <AppTable
          isLoading={isLoading}
          keyId="reportEmailReceiverId"
          columns={columnsEmail}
          data={data}
          pagination={pagination}
          onChangePage={changePage}
          onChangeLimit={changeLimit}
        />
      </div>
    );
  };

  const _renderTemplateEmail = () => {
    return (
      <div style={{ marginTop: 15 }}>
        <RowFilter
          filterValues={{
            search: pageQueries.search,
            status: pageQueries.filterStatus || 'All',
            filterData: pageQueries.filterData || 'default',
            company: company.name || 'all',
          }}
          filters={[filterData]}
        />
        <AppTable
          isLoading={isLoading}
          keyId="templateId"
          columns={columnsTemplate}
          data={data}
          pagination={pagination}
          onChangePage={changePage}
          onChangeLimit={changeLimit}
        />
      </div>
    );
  };

  const _renderEmailTargetAsset = () => {
    const emailTargetAssetTableData: ReportEmailTargetAssetProperties[] =
      isLoading
        ? []
        : data.filter((item: ReportEmailTargetAssetProperties) =>
            Boolean(item.asset),
          );

    return (
      <div style={{ marginTop: 15 }}>
        <RowFilter
          filterValues={{
            search: pageQueries.search,
            status: pageQueries.filterStatus || 'All',
            filterData: pageQueries.filterData || 'default',
            company: company.name || 'all',
          }}
          filters={[filterData]}
        />
        <AppTable
          isLoading={isLoading}
          keyId="reportEmailTargetAssetId"
          columns={columnsEmailTargetAsset}
          data={emailTargetAssetTableData}
          pagination={pagination}
          onChangePage={changePage}
          onChangeLimit={changeLimit}
        />
      </div>
    );
  };

  const items: any = [
    {
      label: `Email Template`,
      key: 1,
      children: _renderTemplateEmail(),
    },
    // {
    //   label: `Email Receivers`,
    //   key: 2,
    //   children: _renderEmailReceivers(),
    // },
    {
      label: `Email Target by Asset`,
      key: 3,
      children: _renderEmailTargetAsset(),
    },
    {
      label: `Email Logs`,
      key: 4,
      children: <EmailLog ref={emailLogRef} />,
    },
  ];

  useEffect(() => {
    if (activeTab === 4) {
      emailLogRef.current?.refetch();
    } else {
      setIsLoading(true);
      handleSearch('');
      fetchList();
    }
  }, [activeTab]);

  // useEffect(() => {
  //   if (activeTab === 1 && auth()?.role?.roleId !== SUPER_ADMIN_ROLE_ID) {
  //     const companyIds = auth()?.companies?.map((value: any) => {
  //       return value.companyId;
  //     });

  //     if (companyIds && companyIds.length > 0) {
  //       setData((prevData) =>
  //         prevData.filter((item) => companyIds.includes(item.externalId))
  //       );
  //     }
  //   }
  // }, [data]);

  const [form] = Form.useForm();
  const [customEmailCompanyId, setCustomEmailCompanyId] = useState('');
  const [templateEmails, setTemplateEmails] = useState<
    ReportEmailTemplateProperties[]
  >([]);
  const [loadingAction, setIsLoadingAction] = useState<boolean>(false);
  const [loadingSendCustomEmail, setLoadingSendCustomEmail] =
    useState<boolean>(false);
  const [modalAssets, setModalAssets] = useState<AssetProperties[]>([]);
  const [loadingModalAssets, setLoadingModalAssets] = useState<boolean>(false);
  const [customEmailTemplateType, setCustomEmailTemplateType] =
    useState<string>(ETemplateType.DEFAULT);

  const fetchTemplate = async (params: any) => {
    try {
      setIsLoadingAction(true);
      const res = await httpRequest.get(
        `/report-email-template${generateQueryString({
          ...params,
          templateType: customEmailTemplateType,
        })}`,
      );

      if (res.data.payload.results) {
        setTemplateEmails(res.data.payload.results);
      }
    } catch (error) {
      console.error(getErrorMessage(error));
    } finally {
      setIsLoadingAction(false);
    }
  };

  const fetchModalAssets = async (companyId: string) => {
    try {
      setLoadingModalAssets(true);
      const res = await httpRequest.get(
        `/asset${generateQueryString({ filterCompanyIds: companyId })}`,
      );

      if (res.data.payload.results) {
        setModalAssets(res.data.payload.results);
      }
    } catch (error) {
      console.error(getErrorMessage(error));
    } finally {
      setLoadingModalAssets(false);
    }
  };

  useEffect(() => {
    if (customEmailCompanyId) {
      fetchTemplate({});
      fetchModalAssets(customEmailCompanyId);
    }
  }, [customEmailCompanyId]);

  useEffect(() => {
    if (customEmailCompanyId) {
      fetchTemplate({});
      form.setFieldsValue({ templateId: undefined });
    }
  }, [customEmailTemplateType]);

  const companyMapped = React.useMemo(() => {
    return companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
  }, [companies]);

  const assetMapped = React.useMemo(() => {
    return modalAssets.map((item) => ({
      value: item.assetId,
      label: item.name,
    }));
  }, [modalAssets]);

  const templateEmailMapped = React.useMemo(() => {
    return templateEmails.map((item) => ({
      value: item.templateId,
      label: item.templateName,
    }));
  }, [templateEmails]);

  const dateValue = Form.useWatch('date', form);

  const handleSendCustomEmail = async (values: any) => {
    try {
      setLoadingSendCustomEmail(true);

      const asset = assetMapped.find((item) => item.value === values.assetId);
      const fullAsset = modalAssets.find(
        (item) => item.assetId === values.assetId,
      );
      const company = companies.find(
        (item) => item.companyId === values.companyId,
      );

      if (asset && company) {
        const data = {
          assetId: values.assetId,
          assetName: asset.label,
          companyId: values.companyId,
          companyName: company.name,
          companyDataType: company.dataType,
          startAt: dayjs(
            `${dayjs(values.date).format('YYYY-MM-DD')} ${dayjs(values.startTime).format('HH:mm')}`,
            'YYYY-MM-DD HH:mm',
          ).unix(),
          endAt: dayjs(
            `${dayjs(values.date).format('YYYY-MM-DD')} ${dayjs(values.endTime).format('HH:mm')}`,
            'YYYY-MM-DD HH:mm',
          ).unix(),
          timezone: fullAsset?.timezone ?? 7,
          receiverEmail: values.emailTarget,
          templateId: values.templateId,
          interval: 'minute',
          sentBy: auth()?.email,
        };

        await httpRequest.post<BaseResponseProps<any>>(
          '/scheduler/send-to-email',
          data,
        );

        form.resetFields();

        setIsModalCustomEmailOpen(false);
        setModalAssets([]);
        setCustomEmailCompanyId('');
        setCustomEmailTemplateType(ETemplateType.DEFAULT);

        emailLogRef.current?.refetch();

        message.open({
          type: 'success',
          content: `Success send report email for ${asset.label}`,
        });
      } else {
        message.open({
          type: 'error',
          content: 'Failed send custom email report. Code 11',
        });
      }
    } catch (error) {
      console.error('Failed send email report', error);

      const statusCode = getErrorStatus(error);
      const errorMessage = await getHttpErrorMessage(error);
      const normalizedMessage = (errorMessage || '').toLowerCase();

      if (
        statusCode === 400 ||
        normalizedMessage.includes('no data') ||
        normalizedMessage.includes('data completion')
      ) {
        Modal.warning({
          title: 'No Data',
          content: errorMessage || DEFAULT_NO_DATA_CUSTOM_EMAIL_MESSAGE,
          okText: 'OK',
        });
        return;
      }

      Modal.error({
        title: 'Send Failed',
        content:
          errorMessage || 'Failed send custom email report. Please try again.',
        okText: 'OK',
      });
    } finally {
      setLoadingSendCustomEmail(false);
    }
  };

  const CustomEmailModal = isMobile
    ? MasterReportMobileCustomEmailModal
    : Modal;
  const CustomEmailBodyWrapper = isMobile
    ? MasterReportMobileCustomEmailBody
    : React.Fragment;

  return (
    <React.Fragment>
      {isMobile ? (
        <MasterReportMobilePage>
          <MasterReportMobileHeader
            title="Master Report Email"
            onBack={() => navigate(-1)}
            action={
              isUserHasPermission([FEATURE + '.CREATE']) ? (
                <MasterReportMobileActionButton
                  type="primary"
                  onClick={() => {
                    if (activeTabKey === 4) {
                      setIsModalCustomEmailOpen(true);
                      return;
                    }

                    handleCreate();
                  }}
                >
                  {activeTabKey === 4 ? 'Send Mail' : 'Add'}
                </MasterReportMobileActionButton>
              ) : undefined
            }
          />

          <MasterReportMobileTabs>
            {[
              { key: 1, label: 'Email Template' },
              { key: 3, label: 'Email Target by Asset' },
              { key: 4, label: 'Email Logs' },
            ].map((item) => (
              <MasterReportMobileTabButton
                key={item.key}
                type="button"
                $active={activeTabKey === item.key}
                onClick={() => setActiveTab(item.key)}
              >
                {item.label}
              </MasterReportMobileTabButton>
            ))}
          </MasterReportMobileTabs>

          {renderMobileListContent()}
        </MasterReportMobilePage>
      ) : (
        <>
          <HeaderSection
            title={'Master Report Email'}
            rightAction={
              activeTab === 4
                ? [
                    isUserHasPermission([FEATURE + '.CREATE']) ? (
                      <Button
                        type="primary"
                        onClick={() => setIsModalCustomEmailOpen(true)}
                      >
                        Send Custom Report Email
                      </Button>
                    ) : (
                      false
                    ),
                  ]
                : activeTab !== 2
                  ? [
                      isUserHasPermission([FEATURE + '.CREATE']) ? (
                        <Button type="primary" onClick={handleCreate}>
                          Add{' '}
                          {activeTab === 1
                            ? 'Template'
                            : activeTab === 3
                              ? 'Target'
                              : ''}
                        </Button>
                      ) : (
                        false
                      ),
                    ]
                  : undefined
            }
          />

          <Tabs
            // type='card'
            // defaultActiveKey={`${queryParams.get('activeTab') || activeTab}`}
            onChange={(e) => setActiveTab(Number(e))}
            items={items}
          />
        </>
      )}

      <CustomEmailModal
        title={
          isMobile ? (
            <MasterReportMobileCustomEmailTitle>
              <h2>Custom Mail</h2>
              <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit</p>
            </MasterReportMobileCustomEmailTitle>
          ) : (
            'Send Custom Email'
          )
        }
        open={isModalCustomEmailOpen}
        onOk={() => form.submit()}
        okText="Send Email"
        confirmLoading={loadingSendCustomEmail}
        footer={isMobile ? null : undefined}
        closable={!isMobile}
        centered={isMobile}
        width={isMobile ? 'calc(100vw - 24px)' : undefined}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={() => {
          setIsModalCustomEmailOpen(false);
          form.resetFields();
          setModalAssets([]);
          setCustomEmailCompanyId('');
          setCustomEmailTemplateType(ETemplateType.DEFAULT);
        }}
      >
        <CustomEmailBodyWrapper>
          <Form
            form={form}
            name="sendCustomEmail"
            layout="vertical"
            onFinish={handleSendCustomEmail}
            autoComplete="off"
          >
            <Row>
              <CustomFormItemPer2
                label="Company"
                name="companyId"
                rules={generateFormRules('Company', ['required'])}
              >
                <Select
                  showSearch
                  placeholder="Choose company"
                  style={{ width: '100%' }}
                  options={companyMapped}
                  onChange={(e) => {
                    setCustomEmailCompanyId(e);

                    form.setFieldsValue({
                      assetId: undefined,
                      templateId: undefined,
                    });
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label="Asset"
                name="assetId"
                rules={generateFormRules('Asset', ['required'])}
              >
                <Select
                  disabled={customEmailCompanyId ? false : true}
                  showSearch
                  loading={loadingModalAssets}
                  placeholder="Choose asset"
                  style={{ width: '100%' }}
                  options={assetMapped}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer2
                label="Template Type"
                name="customTemplateType"
                initialValue={ETemplateType.DEFAULT}
              >
                <Select
                  placeholder="Choose template type"
                  style={{ width: '100%' }}
                  options={TEMPLATE_TYPE_OPTIONS}
                  onChange={(value: string) =>
                    setCustomEmailTemplateType(value)
                  }
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label="Template Email"
                name="templateId"
                rules={generateFormRules('Template Email', ['required'])}
              >
                <Select
                  disabled={customEmailCompanyId ? false : true}
                  showSearch
                  loading={loadingAction}
                  placeholder="Choose template"
                  style={{ width: '100%' }}
                  options={templateEmailMapped}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer1
                label="Date"
                name="date"
                rules={generateFormRules('Date', ['required'])}
              >
                <DatePicker format={'YYYY-MM-DD'} style={{ width: '100%' }} />
              </CustomFormItemPer1>
            </Row>
            <Row>
              <CustomFormItemPer2
                label="Start Time"
                name="startTime"
                rules={generateFormRules('Start Time', ['required'])}
              >
                <TimePicker
                  format={'HH:mm'}
                  style={{ width: '100%' }}
                  disabled={!dateValue}
                />
              </CustomFormItemPer2>
              <CustomFormItemPer2
                label="End Time"
                name="endTime"
                rules={generateFormRules('Start Time', ['required'])}
              >
                <TimePicker
                  format={'HH:mm'}
                  style={{ width: '100%' }}
                  disabled={!dateValue}
                />
              </CustomFormItemPer2>
            </Row>
            <Row>
              <CustomFormItemPer1
                label="Email Target"
                name="emailTarget"
                rules={generateFormRules('Email Target', ['required'])}
                help="Separate with coma for multiple email target"
              >
                <Input placeholder="Input email target" />
              </CustomFormItemPer1>
            </Row>
            {isMobile ? (
              <MasterReportMobileCustomEmailActions>
                <MasterReportMobileCustomEmailCancelButton
                  onClick={() => {
                    setIsModalCustomEmailOpen(false);
                    form.resetFields();
                    setModalAssets([]);
                    setCustomEmailCompanyId('');
                    setCustomEmailTemplateType(ETemplateType.DEFAULT);
                  }}
                >
                  Cancel
                </MasterReportMobileCustomEmailCancelButton>
                <MasterReportMobileCustomEmailSubmitButton
                  type="primary"
                  loading={loadingSendCustomEmail}
                  onClick={() => form.submit()}
                >
                  Send Email
                </MasterReportMobileCustomEmailSubmitButton>
              </MasterReportMobileCustomEmailActions>
            ) : null}
          </Form>
        </CustomEmailBodyWrapper>
      </CustomEmailModal>

      {isMobile && activeTabKey !== 4 && (
        <MasterReportMobileFilterDrawer
          title="Filter"
          placement="bottom"
          height="auto"
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <MasterReportMobileFilterForm>
            <MasterReportMobileFilterFields>
              {activeTabKey !== 1 ? (
                <MasterReportMobileFilterField>
                  Company
                  <MasterReportMobileFilterSelect
                    value={mobileFilterValues.company}
                    options={filterCompanyOptions}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        company: String(value),
                      }))
                    }
                  />
                </MasterReportMobileFilterField>
              ) : null}
              <MasterReportMobileFilterField>
                Status
                <MasterReportMobileFilterSelect
                  value={mobileFilterValues.status}
                  options={
                    activeTabKey === 1
                      ? [
                          { value: 'All', label: 'All' },
                          { value: 'true', label: 'Active' },
                          { value: 'false', label: 'Inactive' },
                        ]
                      : [
                          { value: 'All', label: 'All' },
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                        ]
                  }
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      status: String(value),
                    }))
                  }
                />
              </MasterReportMobileFilterField>
              {activeTabKey === 1 ? (
                <MasterReportMobileFilterField>
                  Template Type
                  <MasterReportMobileFilterSelect
                    value={mobileFilterValues.templateType}
                    options={[
                      { value: 'All', label: 'All' },
                      ...TEMPLATE_TYPE_OPTIONS,
                    ]}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        templateType: String(value),
                      }))
                    }
                  />
                </MasterReportMobileFilterField>
              ) : null}
              {isUserHasPermission([FEATURE + '.DELETE']) ? (
                <MasterReportMobileFilterField>
                  Filter Data
                  <MasterReportMobileFilterSelect
                    value={mobileFilterValues.filterData}
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'show', label: 'Include Archive Data' },
                    ]}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        filterData: String(value),
                      }))
                    }
                  />
                </MasterReportMobileFilterField>
              ) : null}
            </MasterReportMobileFilterFields>
            <MasterReportMobileFilterActions>
              <MasterReportMobileFilterApplyButton
                type="primary"
                onClick={handleApplyMobileFilter}
              >
                Apply
              </MasterReportMobileFilterApplyButton>
            </MasterReportMobileFilterActions>
          </MasterReportMobileFilterForm>
        </MasterReportMobileFilterDrawer>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            {activeTab === 1
              ? 'Are you sure want to delete this data?. Make sure this template is not used by any report otherwise the schedule report of asset that using this report wont work'
              : 'Are you sure want to delete this data? This data will be archived and only User with restore permission can undo this action.'}
          </p>
        )}
      </Modal>

      <Modal
        title="Restore Confirmation"
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>
    </React.Fragment>
  );
};
export default MasterReport;
